import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PublicHeader from '../../components/PublicHeader';
import PublicFooter from '../../components/PublicFooter';
import PublicCouponBanner from '../../components/PublicCouponBanner';
import '../../pcos-management-style.css';
import { apiGet, apiPost } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useCountry } from '../../context/CountryContext';
import { useNotification } from '../../context/CustomDialogContext';
import { useLeadModal } from '../../context/LeadModalContext';
import CountryPhoneInput from '../../components/CountryPhoneInput';
import CourseEnrollModal from '../../components/CourseEnrollModal';
import courseBgImage from '../../assets/images/courses/course_bg.webp';
import heroCourseImage from '../../assets/images/courses/95-hour-kids-yoga-ttc.webp';

/* ─── Accordion Item (Syllabus & FAQ) ─────────────────────────────────────── */
const AccordionItem = ({ num, title, subtitle, children, isActive, onToggle }) => {
  const contentRef = useRef(null);
  const [height, setHeight] = useState('0px');

  useEffect(() => {
    setHeight(isActive ? `${contentRef.current ? contentRef.current.scrollHeight : 500}px` : '0px');
  }, [isActive]);

  return (
    <div className={`fy-accordion-item ${isActive ? 'active' : ''}`}>
      <button
        className="fy-accordion-btn"
        onClick={onToggle}
        aria-expanded={isActive}
        type="button"
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
          {num && <span className="fy-accordion-num">{num}</span>}
          <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <span style={{ fontWeight: 700 }}>{title}</span>
            {subtitle && <span className="fy-accordion-subtitle">{subtitle}</span>}
          </span>
        </span>
        <div className="fy-accordion-icon"><i className="bi bi-plus"></i></div>
      </button>
      <div className="fy-accordion-content" style={{ maxHeight: height }}>
        <div ref={contentRef} className="fy-accordion-inner">
          {children}
        </div>
      </div>
    </div>
  );
};

/* ─── Main Component ──────────────────────────────────────────────────────── */
const KidsYoga = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedCountry, formatPrice } = useCountry();
  const { showAlert } = useNotification();
  const { openModal } = useLeadModal();

  const [isEnrollModalOpen, setIsEnrollModalOpen] = useState(false);

  const courseTitle = "95-Hour Kids Yoga Teacher Training";

  const getLogoUrl = () => {
    const apiBase = import.meta.env.VITE_API_BASE_URL || '/my-lms/backend/api';
    return `${apiBase}/logo.php`;
  };

  const [activeModule, setActiveModule] = useState(0);
  const [activeFaq, setActiveFaq] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    father_name: '',
    address: '',
    countryCode: '+91',
    phone: '',
    email: '',
    batch_id: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const [dbCourse, setDbCourse] = useState(null);
  const [demoInfo, setDemoInfo] = useState(null);
  const [upcomingBatches, setUpcomingBatches] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(true);
  const [activeCoupons, setActiveCoupons] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());

  /* Fetch active coupons */
  useEffect(() => {
    async function fetchCoupons() {
      try {
        let couponsRes = await apiGet('/public-banner-coupons.php');
        if (!couponsRes || (Array.isArray(couponsRes) && couponsRes.length === 0) || (couponsRes.data && couponsRes.data.length === 0)) {
          couponsRes = await apiGet('/public-coupons.php');
        }
        let list = [];
        if (Array.isArray(couponsRes)) {
          list = couponsRes;
        } else if (couponsRes && Array.isArray(couponsRes.data)) {
          list = couponsRes.data;
        } else if (couponsRes && Array.isArray(couponsRes.list)) {
          list = couponsRes.list;
        }
        setActiveCoupons(list);
      } catch (err) {
        console.error('Failed to fetch coupons:', err);
      }
    }
    fetchCoupons();
  }, []);

  /* 1-second interval for real-time coupon countdown */
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const parseDateSafe = (dateStr) => {
    if (!dateStr) return null;
    let cleanStr = String(dateStr).trim();
    if (!cleanStr) return null;

    let d = new Date(cleanStr);
    if (!isNaN(d.getTime())) return d;

    cleanStr = cleanStr.replace('T', ' ').replace(/-/g, '/');
    d = new Date(cleanStr);
    if (!isNaN(d.getTime())) return d;

    d = new Date(cleanStr.replace(/ /g, 'T'));
    if (!isNaN(d.getTime())) return d;

    return null;
  };

  const getBannerCouponForKidsYoga = () => {
    if (!dbCourse) return null;

    const match = activeCoupons.find(
      (c) => Number(c.course_id) === Number(dbCourse.id) || Number(c.course_id) === 0
    );
    if (!match) return null;

    if (match.status && match.status !== 'active') return null;

    const isShowOnPages = match.show_on_pages === undefined || match.show_on_pages === null || match.show_on_pages === 1 || match.show_on_pages === '1' || match.show_on_pages === true;
    if (!isShowOnPages) return null;

    const now = currentTime.getTime();

    if (match.banner_expiry) {
      const expDate = parseDateSafe(match.banner_expiry);
      if (expDate && expDate.getTime() - now <= 0) return null;
    }
    if (match.banner_start) {
      const startDate = parseDateSafe(match.banner_start);
      if (startDate && now < startDate.getTime()) return null;
    }
    if (match.expiry_date) {
      const expDate = parseDateSafe(match.expiry_date);
      if (expDate && expDate.getTime() - now <= 0) return null;
    }

    return match;
  };

  const formatCountdown = (expiryDateStr) => {
    const expDate = parseDateSafe(expiryDateStr);
    if (!expDate) return '';

    const difference = expDate.getTime() - currentTime.getTime();
    if (difference <= 0) return 'Expired';

    const days = Math.floor(difference / (1000 * 60 * 60 * 24));
    const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((difference % (1000 * 60)) / 1000);

    const parts = [];
    if (days > 0) {
      parts.push(`${days} day${days > 1 ? 's' : ''}`);
    }
    parts.push(`${hours} hr`);
    parts.push(`${minutes} min`);
    parts.push(`${seconds} sec`);

    return parts.join(' ');
  };

  /* Scoped CSS class for design system */
  useLayoutEffect(() => {
    document.body.classList.add('pcos-page');
    return () => { document.body.classList.remove('pcos-page'); };
  }, []);

  /* Load Kids Yoga course data & batches & demo availability */
  useEffect(() => {
    async function loadData() {
      try {
        const coursesData = await apiGet('/public-courses.php');
        let currentCourseId = 0;
        let targetTitle = courseTitle;
        if (coursesData && Array.isArray(coursesData)) {
          let match = coursesData.find(
            c => c.title && (c.title.toLowerCase().trim() === courseTitle.toLowerCase().trim() || c.title.toLowerCase().trim() === 'kids yoga teacher training')
          );
          if (!match) {
            match = coursesData.find(
              c => (c.slug && c.slug.toLowerCase().trim() === '95-hour-kids-yoga-teacher-training') ||
                   (c.course_slug && c.course_slug.toLowerCase().trim() === '95-hour-kids-yoga-teacher-training') ||
                   (c.title && c.title.toLowerCase().includes('kids yoga'))
            );
          }
          if (match) {
            setDbCourse(match);
            currentCourseId = match.id;
            if (match.title) {
              targetTitle = match.title;
            }
          }
        }

        // Query upcoming batches for Kids Yoga
        let batchesData = await apiGet(`/get-upcoming-batches.php?course_title=${encodeURIComponent(targetTitle)}`);
        
        if ((!batchesData || !Array.isArray(batchesData) || batchesData.length === 0) && targetTitle !== courseTitle) {
          const fallbackBatches = await apiGet(`/get-upcoming-batches.php?course_title=${encodeURIComponent(courseTitle)}`);
          if (fallbackBatches && Array.isArray(fallbackBatches) && fallbackBatches.length > 0) {
            batchesData = fallbackBatches;
          }
        }

        if ((!batchesData || !Array.isArray(batchesData) || batchesData.length === 0) && currentCourseId > 0) {
          const idBatches = await apiGet(`/get-upcoming-batches.php?course_id=${currentCourseId}`);
          if (idBatches && Array.isArray(idBatches) && idBatches.length > 0) {
            batchesData = idBatches;
          }
        }

        setUpcomingBatches(batchesData || []);

        try {
          if (currentCourseId > 0) {
            const demoRes = await apiGet(`/public/check-demo-course.php?course_id=${currentCourseId}&slug=95-hour-kids-yoga-teacher-training`);
            if (demoRes && demoRes.available) {
              setDemoInfo(demoRes);
            }
          }
        } catch (err) {
          console.log("No demo configuration for this course.");
        }
      } catch (err) {
        console.error('Failed to load course details & batches:', err);
      } finally {
        setLoadingBatches(false);
      }
    }
    loadData();
  }, []);

  /* Sync logged-in student data */
  useEffect(() => {
    if (user && user.role === 'student') {
      setFormData(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || '',
        countryCode: user.country_code || '+91',
        phone: user.mobile_number || user.phone || '',
        father_name: user.father_name || '',
        address: user.address || ''
      }));
    }
  }, [user]);

  /* SEO & smooth scroll */
  useEffect(() => {
    document.title = "95-Hour Kids Yoga Teacher Training | PranaFlow Academy";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content',
        "Join PranaFlow Academy's 95-Hour Kids Yoga Teacher Training and learn child-friendly yoga, breathing practices, mindfulness, yoga games, storytelling, safety, and teaching methodology for children.");
    }

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', "https://pranaflowacademy.com/courses/95-hour-kids-yoga-teacher-training");

    // Open Graph Metadata
    const ogTags = [
      { property: 'og:title', content: "95-Hour Kids Yoga Teacher Training | PranaFlow Academy" },
      { property: 'og:description', content: "Join PranaFlow Academy's 95-Hour Kids Yoga Teacher Training and learn child-friendly yoga, breathing practices, mindfulness, yoga games, storytelling, safety, and teaching methodology for children." },
      { property: 'og:type', content: "website" },
      { property: 'og:url', content: "https://pranaflowacademy.com/courses/95-hour-kids-yoga-teacher-training" },
      { property: 'og:image', content: "https://pranaflowacademy.com/images/courses/95-hour-kids-yoga-ttc.webp" },
      { property: 'og:site_name', content: "PranaFlow Academy" }
    ];

    ogTags.forEach(tag => {
      let el = document.querySelector(`meta[property="${tag.property}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', tag.property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', tag.content);
    });

    // Twitter / X Metadata
    const twitterTags = [
      { name: 'twitter:card', content: "summary_large_image" },
      { name: 'twitter:title', content: "95-Hour Kids Yoga Teacher Training | PranaFlow Academy" },
      { name: 'twitter:description', content: "Join PranaFlow Academy's 95-Hour Kids Yoga Teacher Training and learn child-friendly yoga, breathing practices, mindfulness, yoga games, storytelling, safety, and teaching methodology for children." },
      { name: 'twitter:image', content: "https://pranaflowacademy.com/images/courses/95-hour-kids-yoga-ttc.webp" }
    ];

    twitterTags.forEach(tag => {
      let el = document.querySelector(`meta[name="${tag.name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', tag.name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', tag.content);
    });

    if (location.hash) {
      const id = location.hash.replace('#', '');
      const el = document.getElementById(id);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.hash, location.pathname]);

  /* Schema injection */
  useEffect(() => {
    const scriptId = 'jsonld-schema-kidsyoga';
    let script = document.getElementById(scriptId);
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    const courseSchema = {
      "@context": "https://schema.org",
      "@type": "Course",
      "@id": "https://pranaflowacademy.com/courses/95-hour-kids-yoga-teacher-training#course",
      "name": "95-Hour Kids Yoga Teacher Training",
      "description": "PranaFlow Academy's 95-Hour Kids Yoga Teacher Training is designed for yoga teachers, parents, educators, and wellness practitioners who want to learn safe, creative, and child-friendly yoga teaching methods for children.",
      "url": "https://pranaflowacademy.com/courses/95-hour-kids-yoga-teacher-training",
      "provider": {
        "@id": "https://pranaflowacademy.com/#organization"
      },
      "courseMode": "online",
      "educationalCredentialAwarded": "Certificate of Completion",
      "inLanguage": "en"
    };

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://pranaflowacademy.com/"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Courses",
          "item": "https://pranaflowacademy.com/courses"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": "95-Hour Kids Yoga Teacher Training",
          "item": "https://pranaflowacademy.com/courses/95-hour-kids-yoga-teacher-training"
        }
      ]
    };

    script.textContent = JSON.stringify([courseSchema, breadcrumbSchema]);

    return () => {
      const existingScript = document.getElementById(scriptId);
      if (existingScript) existingScript.remove();
    };
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const displayPrice = dbCourse && parseFloat(dbCourse.fee_inr) > 0 ? formatPrice(dbCourse.fee_inr, dbCourse) : (dbCourse ? 'Fee will be updated soon' : 'Loading...');

  const handleEnrollSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { showAlert({ title: "Warning", message: "Student Name is required.", type: "warning" }); return; }
    if (!formData.email.trim()) { showAlert({ title: "Warning", message: "Email address is required.", type: "warning" }); return; }
    if (!formData.countryCode.trim()) { showAlert({ title: "Warning", message: "Country Code is required.", type: "warning" }); return; }
    if (!formData.phone.trim()) { showAlert({ title: "Warning", message: "Please enter your mobile number.", type: "warning" }); return; }

    const isInactive = dbCourse && dbCourse.status === 'inactive';

    if (isInactive) {
      setIsSubmitting(true);
      try {
        const payload = {
          name: formData.name.trim(),
          father_name: formData.father_name ? formData.father_name.trim() : null,
          mobile: formData.phone.trim(),
          country_code: formData.countryCode.trim(),
          mobile_number: formData.phone.trim(),
          country_iso_code: selectedCountry ? selectedCountry.country_code : 'IN',
          email: formData.email.trim(),
          address: formData.address ? formData.address.trim() : null,
          course_id: dbCourse ? dbCourse.id : null,
          country: selectedCountry ? selectedCountry.country_name : null,
          message: "I am interested in this course. Please contact me when new batches are available.",
          source_page: window.location.pathname
        };

        await apiPost('/save-interested-lead.php', payload);
        setIsSuccess(true);
      } catch (err) {
        showAlert({ title: "Error", message: err.message || "Failed to submit details. Please try again.", type: "error" });
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    if (!formData.father_name.trim()) { showAlert({ title: "Warning", message: "Father's / Guardian's Name is required.", type: "warning" }); return; }
    if (!formData.address.trim()) { showAlert({ title: "Warning", message: "Full Address is required.", type: "warning" }); return; }
    if (!formData.batch_id) { showAlert({ title: "Warning", message: "Please select an upcoming batch.", type: "warning" }); return; }

    const selectedBatch = upcomingBatches.find(b => Number(b.id) === Number(formData.batch_id));
    if (!selectedBatch) { showAlert({ title: "Warning", message: "Selected batch not found.", type: "warning" }); return; }

    navigate('/enroll-confirm', {
      state: {
        studentDetails: {
          name: formData.name,
          father_name: formData.father_name,
          address: formData.address,
          countryCode: formData.countryCode,
          phone: formData.phone,
          email: formData.email
        },
        courseDetails: {
          id: dbCourse ? dbCourse.id : null,
          title: dbCourse ? dbCourse.title : courseTitle,
          duration: dbCourse ? dbCourse.duration : '95 Hours',
          fee_inr: dbCourse ? dbCourse.fee_inr : 0,
          fee_usd: dbCourse ? dbCourse.fee_usd : 0
        },
        batchDetails: {
          id: selectedBatch.id,
          name: selectedBatch.batch_name,
          timing: selectedBatch.batch_timing,
          start_date: selectedBatch.start_date
        }
      }
    });
  };

  /* 12 Modules Syllabus Data */
  const modules = [
    {
      num: '01',
      title: 'Introduction to Kids Yoga',
      desc: 'Understand the meaning, purpose, and importance of yoga for children. Learn how kids yoga is different from adult yoga and why children need a playful, creative, and supportive teaching approach.'
    },
    {
      num: '02',
      title: 'Child Development and Age-Based Teaching',
      desc: "Learn about different age groups and how children’s bodies, minds, emotions, and attention spans develop. Understand how to plan classes for toddlers, young children, pre-teens, and teenagers."
    },
    {
      num: '03',
      title: 'Kids Yoga Asanas',
      desc: 'Learn child-friendly yoga postures using animals, nature, stories, and imagination. Understand safe alignment, simple instructions, modifications, and how to avoid forceful or unsafe practice.'
    },
    {
      num: '04',
      title: 'Creative Class Themes and Storytelling',
      desc: 'Learn how to create engaging yoga stories using themes such as jungle, ocean, space, nature, seasons, animals, festivals, emotions, and daily life. You will learn how to turn yoga into a joyful learning experience.'
    },
    {
      num: '05',
      title: 'Yoga Games and Group Activities',
      desc: 'Discover fun yoga games, partner activities, circle games, memory games, balance games, and movement-based activities that keep children active, focused, and connected.'
    },
    {
      num: '06',
      title: 'Breathing Practices for Children',
      desc: 'Learn safe and simple breathing techniques for children. Understand how to teach breath awareness through playful methods such as balloon breathing, bee breath, flower breathing, and calming breath practices.'
    },
    {
      num: '07',
      title: 'Mindfulness and Relaxation for Kids',
      desc: 'Learn how to introduce mindfulness, body awareness, short meditation, guided relaxation, gratitude, and calming practices in a child-friendly way.'
    },
    {
      num: '08',
      title: 'Emotional Balance and Yoga Values',
      desc: 'Understand how yoga can support confidence, kindness, patience, focus, self-awareness, emotional expression, and positive behavior in children. Learn how to teach yogic values without making the class boring or too serious.'
    },
    {
      num: '09',
      title: 'Classroom Management and Teaching Skills',
      desc: 'Learn how to manage children’s energy, handle distractions, maintain discipline with kindness, give clear instructions, create boundaries, and keep the class safe and engaging.'
    },
    {
      num: '10',
      title: 'Kids Yoga Class Planning',
      desc: 'Learn how to structure a complete kids yoga class from opening to closing. You will practice creating lesson plans for different age groups, class durations, and teaching environments.'
    },
    {
      num: '11',
      title: 'Teaching Practice and Practicum',
      desc: 'Practice teaching short kids yoga sessions, receive feedback, improve your confidence, and learn how to present yourself as a professional kids yoga teacher.'
    },
    {
      num: '12',
      title: 'Final Assessment and Certification',
      desc: 'Complete your assignments, class plan, teaching practice, and final evaluation to receive your certificate of completion from PranaFlow Academy.'
    }
  ];

  /* 6 Core Pillars */
  const pillars = [
    {
      num: '01',
      title: 'Understand the Child',
      tags: ['Developmental Stages', 'Attention Spans', 'Child Psychology'],
      desc: 'Learn how children’s bodies, minds, and emotions grow across toddler, early childhood, and pre-teen stages.'
    },
    {
      num: '02',
      title: 'Make Yoga Safe & Age Appropriate',
      tags: ['Animal Asanas', 'Safe Alignment', 'Anatomy & Growth'],
      desc: 'Teach safe, imaginative postures using animals, nature, and stories without forceful adult movement.'
    },
    {
      num: '03',
      title: 'Make Yoga Fun',
      tags: ['Storytelling Themes', 'Yoga Games', 'Group Movement'],
      desc: 'Transform classes into joyful adventures using jungle, space, ocean, and festival theme stories and games.'
    },
    {
      num: '04',
      title: 'Teach Calm Through Playful Tools',
      tags: ['Balloon Breath', 'Bee Breath', 'Mindfulness & Savasana'],
      desc: 'Guide child-friendly breathwork, sensory mindfulness, guided relaxation, and emotional balance tools.'
    },
    {
      num: '05',
      title: 'Classroom Management with Kindness',
      tags: ['Energy Direction', 'Clear Boundaries', 'Positive Discipline'],
      desc: 'Manage high energy, restless children, and class distractions while maintaining a supportive, warm space.'
    },
    {
      num: '06',
      title: 'Practise Becoming the Teacher',
      tags: ['Lesson Planning', 'Teaching Practicum', 'Assessment & Cert'],
      desc: 'Build lesson plans, deliver live teaching practice, receive expert feedback, and earn your 95-hour certificate.'
    }
  ];

  /* Common Teaching Challenges */
  const challengePoints = [
    { title: "Children losing focus quickly", desc: "Short attention spans and high energy levels require dynamic, story-led class structures" },
    { title: "Rigid adult poses causing strain", desc: "Adult postures can strain growing joint cartilage if not modified for playful child anatomy" },
    { title: "Boring explanations & lectures", desc: "Long verbal alignment cues disconnect children; visual stories and games maintain engagement" },
    { title: "Overwhelmed or shy students", desc: "Creating an inclusive space where sensitive children feel safe to participate without pressure" },
    { title: "Restless energy & class disruption", desc: "Channeling high-energy outbursts into constructive movement games rather than strict punishment" },
    { title: "Struggling with class lesson plans", desc: "Unsure how to structure opening warm-ups, main story arc, breath games, and final relaxation" },
    { title: "Difficulty teaching different ages", desc: "A 4-year-old learns through imagination while a 10-year-old needs challenge and group interaction" },
    { title: "Fear of teaching live groups", desc: "Building confidence through practical teaching demonstrations, feedback, and practicum sessions" },
    { title: "Teaching abstract mindfulness", desc: "Replacing complex meditation jargon with balloon breath, flower sensing, and quiet star relaxation" },
    { title: "Integrating yogic values naturally", desc: "Sharing kindness, honesty, and gratitude through stories rather than moralizing lectures" },
    { title: "Managing online vs offline classes", desc: "Adapting interactive movement games for classroom, studio, summer camp, or Zoom teaching" },
    { title: "Lack of specialized certification", desc: "Needing a recognized 95-Hour Kids Yoga Teacher Training credential to approach schools and centers" }
  ];

  const highlights = [
    "95 Hours of Comprehensive Kids Yoga Training",
    "12 Structured Educational Modules",
    "Child Development & Anatomy for Growth Stages",
    "Playful Animal Asanas & Safe Posture Modifications",
    "Creative Storytelling & Themed Class Design",
    "Interactive Yoga Games, Partner & Circle Activities",
    "Child-Friendly Breathing Practices (Balloon, Bee Breath)",
    "Mindfulness, Guided Relaxation & Savasana for Kids",
    "Classroom Management & Positive Energy Direction",
    "Complete Lesson Planning for Different Age Groups",
    "Live Teaching Practice, Practicum & Feedback",
    "PranaFlow Student Dashboard Portal Access",
    "Same-Day Class Video Recordings for Catch-Up",
    "Small Interactive Batches (Max 25 Students)",
    "PranaFlow Academy 95-Hour Kids Yoga Certificate"
  ];

  const bonuses = [
    "Kids Asana Illustration Guide",
    "12-Module Study Manual",
    "50+ Yoga Games & Story Themes",
    "Child Breathing Cards",
    "Lesson Plan Templates",
    "Age-Based Class Sequences",
    "PranaFlow 95-Hour Certificate"
  ];

  const myths = [
    { m: '"Kids yoga is just adult yoga made smaller."', f: 'Children have different skeletal development, attention spans, and emotional needs. Kids yoga uses movement stories, games, imagination, and short calming tools tailored to growing bodies.' },
    { m: '"You need to be an advanced yogi to teach children."', f: 'Children need warm, creative, and patient guides. Clear instructions, playful energy, and child-safe knowledge matter far more than advanced adult postures.' },
    { m: '"Children cannot practice mindfulness or quiet relaxation."', f: 'Children love quiet relaxation when introduced through sensory stories, teddy bear breathing, and guided star journeys appropriate for their age.' },
    { m: '"Kids yoga classes are chaotic and unpredictable."', f: 'With structured theme planning, energy management tools, and clear boundaries, kids yoga classes are engaging, joyful, and well-managed.' },
    { m: '"You can use the same lesson plan for a 4-year-old and a 12-year-old."', f: 'Toddlers learn through animal sounds and movement stories, while pre-teens need physical challenges, teamwork, and deeper discussion of values.' },
    { m: '"Kids yoga is only for athletic or quiet children."', f: 'Kids yoga is non-competitive and inclusive. It helps active kids channel energy while helping shy kids build self-confidence.' }
  ];

  const diffPoints = [
    { title: "95 Hours Dedicated Curriculum", desc: "A comprehensive 12-module specialization focused entirely on teaching children aged 3–15." },
    { title: "Child Development Focus", desc: "Grounded in child psychology, growth anatomy, motor skill development, and age-appropriate teaching." },
    { title: "50+ Story Themes & Games", desc: "Master practical storytelling arcs, partner movement, balance games, and creative theme planning." },
    { title: "PranaFlow Student Dashboard", desc: "All course recordings, schedule, study guides, and lesson templates organized in your portal." },
    { title: "Same-Day Recording Access", desc: "Missed a live session? Watch recorded lectures uploaded to your dashboard on the same day." },
    { title: "Practical Teaching Practicum", desc: "Deliver practice teaching sessions, receive peer and instructor feedback, and refine your voice." },
    { title: "Small Interactive Batches", desc: "Limited to maximum 25 students per batch for direct guidance and personal mentorship." },
    { title: "Multi-Setting Career Readiness", desc: "Prepares you to teach in schools, preschools, yoga studios, online, summer camps, and private sessions." }
  ];

  const faqs = [
    { q: "Do I need advanced yoga experience to join this course?", a: "No. Advanced yoga experience is not required. Basic yoga knowledge is helpful, but the training is suitable for a wide range of learners including parents and educators." },
    { q: "Can school teachers and parents join this course?", a: "Yes! This course is designed for yoga teachers, school teachers, preschool educators, parents, wellness coaches, and anyone passionate about children's health." },
    { q: "What certificate will I receive upon completion?", a: "After successfully completing the required training hours, attendance, assignments, practicum, teaching requirements, and final assessment, you will receive a genuine 95-Hour Kids Yoga Teacher Training Certificate issued through the applicable verified Yoga Alliance Registered Children’s Yoga School (RCYS) partner." },
    { q: "Where can I teach kids yoga after completing this training?", a: "You can teach in schools, preschools, yoga studios, wellness centers, activity centers, online classes, summer camps, and private family sessions." },
    { q: "Is this course suitable for both online and offline teaching?", a: "Yes. The methodology covers adapting games, stories, and classroom management for both live in-person groups and interactive online sessions." },
    { q: "What happens if I miss a live session?", a: "All live classes are recorded and uploaded to your PranaFlow Student Dashboard on the same day so you can catch up easily." },
    { q: "Can I pay tuition in installments?", a: "Yes, flexible installment payment options are available according to configured course plans." },
    { q: "Does this certificate make me a licensed school teacher or child therapist?", a: "No. This certificate certifies completion of 95 hours of Kids Yoga Teacher Training. School employment or clinical child therapy depends on statutory state regulations and credentials." }
  ];

  return (
    <div className="pcos-page public-site">
      <PublicHeader />

      <main className="main">
        <div className="fy-header-spacer"></div>

        {/* 1. HERO SECTION */}
        <section id="hero" className="fy-section">
          <div className="fy-hero-container">
            <div className="fy-hero-wrapper">
              <div className="row align-items-center gy-4">
                <div className="col-lg-6">
                  <span className="fy-hero-pill">
                    <i className="bi bi-star-fill text-warning me-1"></i> Specialized Teacher Training Course
                  </span>
                  <h1 className="fy-hero-title">95-Hour Kids Yoga Teacher Training</h1>
                  <div className="fy-hero-subhead">Learn to Teach Yoga to Children With Creativity, Confidence &amp; Care</div>

                  <p className="fy-hero-para">
                    Understand Children. Make Yoga Fun. Build the Skills to Lead Safe, Engaging Kids Yoga Classes.
                  </p>

                  <p className="fy-hero-para">
                    The 95-Hour Kids Yoga Teacher Training by PranaFlow Academy is designed for yoga teachers, school teachers, parents, wellness coaches, and anyone who wants to guide children through yoga, movement, mindfulness, breathing, stories, games, relaxation, and emotional balance — with all sessions recorded and managed through your PranaFlow Student Dashboard.
                  </p>

                  <div className="fy-btn-group">
                    <button type="button" className="fy-btn-primary" onClick={() => setIsEnrollModalOpen(true)}>
                      Enroll Now <i className="bi bi-arrow-right-circle-fill"></i>
                    </button>
                    <button type="button" className="fy-btn-secondary" onClick={() => openModal(courseTitle, dbCourse)}>
                      Enquire Now
                    </button>
                    {demoInfo && demoInfo.available && (
                      <button 
                        type="button"
                        className="fy-btn-secondary" 
                        onClick={() => navigate(`/demo-class?course=${demoInfo.courseSlug || '95-hour-kids-yoga-teacher-training'}&course_id=${dbCourse ? dbCourse.id : ''}&demo_course_id=${demoInfo.demoCourseId}`, { state: { course: dbCourse } })}
                      >
                        {demoInfo.demoFee > 0 
                          ? `Demo Class — ₹${Number(demoInfo.demoFee)}` 
                          : 'Demo Class'}
                      </button>
                    )}
                  </div>

                  <div className="fy-trust-badges">
                    <span><i className="bi bi-clock-fill"></i> 95 Hours Training</span>
                    <span><i className="bi bi-journal-check"></i> 12 Modules</span>
                    <span><i className="bi bi-people-fill"></i> Max 25 Per Batch</span>
                    <span><i className="bi bi-award-fill"></i> Academy Certificate</span>
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="fy-hero-img-container">
                    <img src={heroCourseImage} alt="95-Hour Kids Yoga Teacher Training" loading="eager" />
                  </div>
                </div>
              </div>

              {/* Feature Callout Strip */}
              <div className="fy-strip-container">
                <div className="fy-strip-grid">
                  <div className="fy-strip-item">
                    <div className="fy-strip-icon sage"><i className="bi bi-clock-history"></i></div>
                    <div className="fy-strip-label">95 Hours<br />Training</div>
                  </div>
                  <div className="fy-strip-item">
                    <div className="fy-strip-icon blush"><i className="bi bi-journal-bookmark"></i></div>
                    <div className="fy-strip-label">12 Detailed<br />Modules</div>
                  </div>
                  <div className="fy-strip-item">
                    <div className="fy-strip-icon sage"><i className="bi bi-controller"></i></div>
                    <div className="fy-strip-label">Stories &amp;<br />Yoga Games</div>
                  </div>
                  <div className="fy-strip-item">
                    <div className="fy-strip-icon blush"><i className="bi bi-person-check-fill"></i></div>
                    <div className="fy-strip-label">Teaching<br />Practicum</div>
                  </div>
                  <div className="fy-strip-item">
                    <div className="fy-strip-icon sage"><i className="bi bi-speedometer2"></i></div>
                    <div className="fy-strip-label">Student<br />Dashboard</div>
                  </div>
                  <div className="fy-strip-item">
                    <div className="fy-strip-icon blush"><i className="bi bi-credit-card-2-back"></i></div>
                    <div className="fy-strip-label">Installment<br />Available</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 2. OVERVIEW — "Helping Children Thrive in a Distracted World" */}
        <section className="fy-section">
          <div className="fy-container">
            <div className="fy-plan-box">
              <h2 className="fy-serif-heading mb-4 text-center text-lg-start">Helping Children Thrive in a Distracted World</h2>

              <div className="row gy-4 align-items-center">
                <div className="col-lg-6">
                  <p className="fy-hero-para">
                    Children today are growing up in a fast, distracted, and often overstimulating world. Many struggle with restlessness, short attention spans, emotional overwhelm, low confidence, and screen-heavy lifestyles.
                  </p>
                  <p className="fy-hero-para">
                    Yoga offers children a powerful gift: a way to move, breathe, calm down, express themselves, and feel balanced in body and mind. Teaching kids yoga means creating a safe space where children feel happy, included, supported, and confident.
                  </p>

                  <div className="fy-disclaimer-box">
                    <i className="bi bi-info-circle-fill"></i>
                    <div>
                      As a kids yoga teacher, you are not just teaching postures. You are creating experiences that help children feel calmer, stronger, more focused, and connected to themselves.
                    </div>
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="fy-benefit-pill"><i className="bi bi-check-circle-fill"></i><span>Child development stages, growth anatomy &amp; motor skills</span></div>
                  <div className="fy-benefit-pill"><i className="bi bi-check-circle-fill"></i><span>Child-friendly animal asanas, safe alignment &amp; modifications</span></div>
                  <div className="fy-benefit-pill"><i className="bi bi-check-circle-fill"></i><span>50+ creative class themes, storytelling arcs &amp; movement games</span></div>
                  <div className="fy-benefit-pill"><i className="bi bi-check-circle-fill"></i><span>Balloon breath, bee breath &amp; sensory mindfulness for kids</span></div>
                  <div className="fy-benefit-pill"><i className="bi bi-check-circle-fill"></i><span>Classroom management, energy direction &amp; positive discipline</span></div>
                  <div className="fy-benefit-pill"><i className="bi bi-check-circle-fill"></i><span>PranaFlow Student Dashboard with daily video access</span></div>
                  <div className="fy-benefit-pill mb-0"><i className="bi bi-check-circle-fill"></i><span>Live teaching practicum, peer review &amp; 95-hour certification</span></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 3. HIGHLIGHTS & BONUS — "What You Will Cover" */}
        <section id="highlights" className="fy-section fy-section-alt">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <h2 className="fy-serif-heading">What You Will Cover</h2>
              <div className="fy-title-underline"></div>
            </div>

            <div className="row gy-4 align-items-stretch">
              <div className="col-lg-8">
                <div className="fy-highlights-grid">
                  <div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Child Psychology &amp; Development Stages</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Child-Friendly Asanas &amp; Safe Alignment</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Creative Class Themes &amp; Storytelling Arcs</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Interactive Yoga Games &amp; Group Activities</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Playful Breathing (Balloon &amp; Bee Breath)</span></div>
                  </div>

                  <div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Mindfulness &amp; Guided Relaxation for Kids</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Emotional Balance &amp; Yogic Values for Children</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Classroom Management &amp; Positive Discipline</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Age-Based Lesson Planning (Ages 3–15)</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Teaching Practicum &amp; Live Practice</span></div>
                  </div>

                  <div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Online vs Offline Teaching Techniques</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>PranaFlow Student Dashboard Access</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Child Safety, Boundaries &amp; Safeguarding</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Career Opportunities &amp; Class Promotion</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>95-Hour Kids Yoga Teacher Certificate</span></div>
                  </div>
                </div>
              </div>

              <div className="col-lg-4">
                <div className="fy-bonus-card">
                  <div className="fy-bonus-icon"><i className="bi bi-gift-fill"></i></div>
                  <div className="fy-bonus-title">INCLUDED RESOURCES</div>
                  <div className="fy-bonus-sub">Manuals, Games &amp; Templates</div>
                  <ul className="fy-bonus-list">
                    {bonuses.map((b, i) => (
                      <li key={i}><i className="bi bi-check2-circle"></i>{b}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 4. CURRICULUM OVERVIEW — 6 Pillars & 12 Modules Accordion */}
        <section id="curriculum" className="fy-section">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <h2 className="fy-serif-heading">Course Overview — 6 Core Pillars</h2>
              <div className="fy-title-underline"></div>
            </div>

            <div className="fy-curriculum-grid mb-4">
              {pillars.map((p, i) => (
                <div key={i} className="fy-week-card">
                  <div className="fy-week-icon"><i className="bi bi-flower2"></i></div>
                  <span className={`fy-week-badge ${i % 2 === 0 ? 'sage' : 'blush'}`}>{p.num}</span>
                  <h3 className="fy-week-title">{p.title}</h3>
                  <ul className="fy-week-list">
                    {p.tags.map((t, j) => <li key={j}>{t}</li>)}
                  </ul>
                  <p style={{ fontSize: '0.85rem', color: 'var(--fy-text-muted)', marginTop: '10px', lineHeight: 1.5 }}>{p.desc}</p>
                </div>
              ))}
            </div>

            {/* 12-Module Syllabus Accordion */}
            <div className="fy-section-title-wrap mt-5 mb-4">
              <h3 className="fy-serif-heading" style={{ fontSize: '1.6rem' }}>Complete 12-Module Detailed Syllabus</h3>
              <p className="fy-hero-para" style={{ maxWidth: '650px', margin: '8px auto 0' }}>
                Click on any module to view the detailed topics and descriptions in our 95-hour curriculum.
              </p>
            </div>

            <div className="fy-syllabus-container">
              {modules.map((m, idx) => (
                <AccordionItem
                  key={idx}
                  num={m.num}
                  title={m.title}
                  isActive={activeModule === idx}
                  onToggle={() => setActiveModule(activeModule === idx ? null : idx)}
                >
                  <p style={{ fontSize: '0.9rem', color: 'var(--fy-text-main)', lineHeight: 1.65, margin: 0 }}>
                    {m.desc}
                  </p>
                </AccordionItem>
              ))}
            </div>
          </div>
        </section>

        {/* 5. TEACHING PRACTICUM JOURNEY */}
        <section id="practicum" className="fy-section fy-section-alt">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <h2 className="fy-serif-heading">Teaching Practicum — From Learning to Leading</h2>
              <div className="fy-title-underline"></div>
              <p className="fy-hero-para mt-3" style={{ maxWidth: '650px', margin: '12px auto 0' }}>
                Practical teaching experience is built into the training so you graduate with real confidence.
              </p>
            </div>

            <div className="row gy-4 text-center">
              <div className="col-md-4 col-lg-2">
                <div style={{ background: 'var(--fy-cream-card)', border: '1px solid var(--fy-sage-border)', borderRadius: 'var(--fy-radius-md)', padding: '20px 12px' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'var(--fy-sage-light)', color: 'var(--fy-sage-dark)', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>1</div>
                  <h4 style={{ fontSize: '0.95rem', fontWeight: 700, margin: '0 0 4px' }}>Understand</h4>
                  <p style={{ fontSize: '0.8rem', color: 'var(--fy-text-muted)', margin: 0 }}>Learn child psychology &amp; age-based needs</p>
                </div>
              </div>

              <div className="col-md-4 col-lg-2">
                <div style={{ background: 'var(--fy-cream-card)', border: '1px solid var(--fy-sage-border)', borderRadius: 'var(--fy-radius-md)', padding: '20px 12px' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'var(--fy-blush-light)', color: 'var(--fy-blush-dark)', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>2</div>
                  <h4 style={{ fontSize: '0.95rem', fontWeight: 700, margin: '0 0 4px' }}>Plan</h4>
                  <p style={{ fontSize: '0.8rem', color: 'var(--fy-text-muted)', margin: 0 }}>Design story-led class lesson plans</p>
                </div>
              </div>

              <div className="col-md-4 col-lg-2">
                <div style={{ background: 'var(--fy-cream-card)', border: '1px solid var(--fy-sage-border)', borderRadius: 'var(--fy-radius-md)', padding: '20px 12px' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'var(--fy-sage-light)', color: 'var(--fy-sage-dark)', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>3</div>
                  <h4 style={{ fontSize: '0.95rem', fontWeight: 700, margin: '0 0 4px' }}>Demonstrate</h4>
                  <p style={{ fontSize: '0.8rem', color: 'var(--fy-text-muted)', margin: 0 }}>Practice teaching animal poses &amp; games</p>
                </div>
              </div>

              <div className="col-md-4 col-lg-2">
                <div style={{ background: 'var(--fy-cream-card)', border: '1px solid var(--fy-sage-border)', borderRadius: 'var(--fy-radius-md)', padding: '20px 12px' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'var(--fy-blush-light)', color: 'var(--fy-blush-dark)', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>4</div>
                  <h4 style={{ fontSize: '0.95rem', fontWeight: 700, margin: '0 0 4px' }}>Guide</h4>
                  <p style={{ fontSize: '0.8rem', color: 'var(--fy-text-muted)', margin: 0 }}>Deliver live practicum sessions</p>
                </div>
              </div>

              <div className="col-md-4 col-lg-2">
                <div style={{ background: 'var(--fy-cream-card)', border: '1px solid var(--fy-sage-border)', borderRadius: 'var(--fy-radius-md)', padding: '20px 12px' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'var(--fy-sage-light)', color: 'var(--fy-sage-dark)', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>5</div>
                  <h4 style={{ fontSize: '0.95rem', fontWeight: 700, margin: '0 0 4px' }}>Reflect</h4>
                  <p style={{ fontSize: '0.8rem', color: 'var(--fy-text-muted)', margin: 0 }}>Receive peer &amp; instructor feedback</p>
                </div>
              </div>

              <div className="col-md-4 col-lg-2">
                <div style={{ background: 'var(--fy-cream-card)', border: '1px solid var(--fy-sage-border)', borderRadius: 'var(--fy-radius-md)', padding: '20px 12px' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#DCF5E6', color: '#1A5C33', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>6</div>
                  <h4 style={{ fontSize: '0.95rem', fontWeight: 700, margin: '0 0 4px' }}>Improve</h4>
                  <p style={{ fontSize: '0.8rem', color: 'var(--fy-text-muted)', margin: 0 }}>Refine voice &amp; earn certification</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 6. COURSE DETAILS / WHO IT IS FOR & PRICING CARD */}
        <section id="course-details" className="fy-section">
          <div className="fy-container">
            <div className="row gy-5 align-items-stretch">
              {/* Left image */}
              <div className="col-lg-4" style={{ textAlign: 'center' }}>
                <div className="fy-business-image-wrap">
                  <img src={courseBgImage} alt="95-Hour Kids Yoga Teacher Training" className="img-fluid w-100" />
                </div>
              </div>

              {/* Center — Who it is for */}
              <div className="col-lg-4">
                <h2 className="fy-serif-heading mb-3">Designed for a Wide Range of Learners</h2>
                <div className="text-muted fw-bold mb-3" style={{ fontSize: '0.95rem' }}>Who joins this Kids Yoga Teacher Training.</div>

                <p className="fy-hero-para">
                  Yoga teachers wanting to specialize in kids yoga, school teachers, preschool educators, parents, wellness coaches, and anyone who wants to guide children through yoga, movement, and emotional balance.
                </p>

                <div className="d-flex flex-column gap-2 mb-3">
                  <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Yoga teachers specializing in kids yoga</span></div>
                  <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>School teachers &amp; preschool educators</span></div>
                  <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Parents introducing yoga to their children</span></div>
                  <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Fitness, dance &amp; movement coaches</span></div>
                  <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Childcare &amp; wellness professionals</span></div>
                  <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Anyone passionate about children's health</span></div>
                </div>

                <div className="small text-muted fst-italic">
                  *No advanced yoga experience is required. Basic yoga knowledge is helpful, and the training guides you step by step.
                </div>
              </div>

              {/* Right — Pricing card V2 */}
              <div className="col-lg-4">
                {(() => {
                  const coupon = getBannerCouponForKidsYoga();
                  const feeInr = dbCourse ? parseFloat(dbCourse.fee_inr) : 0;
                  let formattedOriginalPrice = displayPrice;
                  let formattedDiscountedPrice = '';
                  let percentageVal = 0;

                  if (coupon && feeInr > 0) {
                    if (coupon.discount_type === 'percentage') {
                      percentageVal = Math.round(parseFloat(coupon.discount_value));
                      const discountedInr = feeInr * (1 - percentageVal / 100);
                      formattedDiscountedPrice = formatPrice(discountedInr, dbCourse);
                      formattedOriginalPrice = formatPrice(feeInr, dbCourse);
                    } else if (coupon.discount_type === 'flat' || coupon.discount_type === 'fixed') {
                      const fixedVal = parseFloat(coupon.discount_value);
                      const discountedInr = Math.max(0, feeInr - fixedVal);
                      percentageVal = Math.round((fixedVal / feeInr) * 100);
                      formattedDiscountedPrice = formatPrice(discountedInr, dbCourse);
                      formattedOriginalPrice = formatPrice(feeInr, dbCourse);
                    }
                  }

                  return (
                    <div className="fy-program-details-card-v2" id="pricing">
                      <h3 className="pcos-pd-heading">PROGRAM DETAILS</h3>
                      <div className="pcos-pd-heading-divider"></div>

                      <div className="pcos-pd-rows-wrap">
                        <div className="pcos-pd-row">
                          <div className="pcos-pd-label">
                            <i className="bi bi-calendar-event"></i>
                            <span>Duration</span>
                          </div>
                          <div className="pcos-pd-val">95 Hours Training</div>
                        </div>

                        <div className="pcos-pd-row">
                          <div className="pcos-pd-label">
                            <i className="bi bi-journal-check"></i>
                            <span>Curriculum</span>
                          </div>
                          <div className="pcos-pd-val">12 Comprehensive Modules</div>
                        </div>

                        <div className="pcos-pd-row">
                          <div className="pcos-pd-label">
                            <i className="bi bi-person-check"></i>
                            <span>Practicum</span>
                          </div>
                          <div className="pcos-pd-val">Teaching Practice Included</div>
                        </div>

                        <div className="pcos-pd-row">
                          <div className="pcos-pd-label">
                            <i className="bi bi-person-badge"></i>
                            <span>Batch Size</span>
                          </div>
                          <div className="pcos-pd-val">Max 25 Students</div>
                        </div>

                        <div className="pcos-pd-row">
                          <div className="pcos-pd-label">
                            <i className="bi bi-speedometer2"></i>
                            <span>Portal</span>
                          </div>
                          <div className="pcos-pd-val">PranaFlow Dashboard</div>
                        </div>

                        <div className="pcos-pd-row">
                          <div className="pcos-pd-label">
                            <i className="bi bi-award-fill"></i>
                            <span>Certificate</span>
                          </div>
                          <div className="pcos-pd-val">95-Hour Kids Yoga Certificate · RCYS Partner</div>
                        </div>
                      </div>

                      <div className="pf-course-promo-badge pf-course-promo-badge-pd">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
                          <line x1="7" y1="7" x2="7.01" y2="7"></line>
                        </svg>
                        <span>Every batch: First 5 students get an extra discount on fee!</span>
                      </div>

                      {coupon ? (
                        <div className="pcos-pd-offer-box">
                          <div className="pcos-pd-offer-pill">
                            <i className="bi bi-star"></i> LIMITED TIME OFFER
                          </div>

                          <div className="pcos-pd-offer-prices-row">
                            <div className="pcos-pd-orig-price-wrap">
                              <span className="pcos-pd-metric-label">ORIGINAL FEE</span>
                              <span className="pcos-pd-orig-price">{formattedOriginalPrice}</span>
                            </div>

                            <div className="pcos-pd-disc-price-wrap">
                              <span className="pcos-pd-metric-label">DISCOUNTED FEE</span>
                              <span className="pcos-pd-disc-price">{formattedDiscountedPrice}</span>
                            </div>

                            <div className="pcos-pd-badge-wrap">
                              <div className="pcos-pd-badge-rect">
                                <span className="pcos-pd-badge-num">{percentageVal}%</span>
                                <span className="pcos-pd-badge-text">OFF</span>
                              </div>
                            </div>
                          </div>

                          <div className="pcos-pd-timer-strip">
                            <i className="bi bi-stopwatch"></i>
                            <span>
                              {formatCountdown(coupon.banner_expiry || coupon.expiry_date).includes('day') ? 'Offer ends in' : 'Offer valid today'} · {formatCountdown(coupon.banner_expiry || coupon.expiry_date)}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="pcos-pd-normal-box">
                          <span className="pcos-pd-normal-label">TUITION FEE</span>
                          <span className="pcos-pd-normal-price">{loadingBatches ? 'Loading...' : displayPrice}</span>
                        </div>
                      )}

                      <button
                        type="button"
                        className="pcos-pd-enroll-btn"
                        onClick={() => setIsEnrollModalOpen(true)}
                      >
                        <span>Enroll Now &amp; Reserve Your Seat</span>
                        <div className="pcos-pd-btn-arrow-circle">
                          <i className="bi bi-arrow-right"></i>
                        </div>
                      </button>

                      <button
                        type="button"
                        className="fy-btn-secondary w-100 mt-2"
                        onClick={() => openModal(courseTitle, dbCourse)}
                        style={{
                          borderRadius: '30px',
                          padding: '12px 20px',
                          fontWeight: 700,
                          fontSize: '0.9rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px'
                        }}
                      >
                        Enquire Now <i className="bi bi-chat-dots"></i>
                      </button>

                      <div className="pcos-pd-next-batch-strip">
                        <i className="bi bi-leaf"></i>
                        <span>Next batch admissions open — inquire now</span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </section>

        {/* 7. CHILDREN DON'T NEED AN ADULT YOGA CLASS MADE SMALLER */}
        <section className="fy-section fy-section-alt">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <h2 className="fy-serif-heading">Children Don't Need an Adult Yoga Class Made Smaller</h2>
              <div className="fy-title-underline"></div>
              <p className="fy-hero-para mt-3" style={{ maxWidth: '700px', margin: '12px auto 0' }}>
                Teaching children requires a different kind of teacher, specialized tools, and creative class management.
              </p>
            </div>

            <div className="fy-pain-grid">
              {challengePoints.map((p, i) => (
                <div key={i} className="fy-pain-card">
                  <span className="fy-pain-x">✕</span>
                  <div>
                    <h4 className="fy-pain-title">{p.title}</h4>
                    <span className="fy-pain-desc">{p.desc}</span>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ background: 'var(--fy-cream-card)', border: '2px dashed var(--fy-sage-border)', borderRadius: 'var(--fy-radius-lg)', padding: '24px 32px', textAlign: 'center', marginTop: '32px' }}>
              <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--fy-sage-primary)', marginBottom: '8px' }}>
                "When you teach kids yoga with the right understanding, you create experiences that help children feel calmer, stronger, and happier."
              </h3>
              <p style={{ color: 'var(--fy-text-muted)', margin: 0, fontSize: '0.95rem' }}>
                Our 95-hour training gives you the structured tools, games, and lesson plans to teach with confidence.
              </p>
            </div>
          </div>
        </section>

        {/* 8. PRANAFLOW STUDENT DASHBOARD */}
        <section className="fy-section fy-dashboard-section">
          <div className="fy-container">
            <div className="row align-items-center gy-5">
              <div className="col-lg-7">
                <span className="fy-dashboard-badge">PranaFlow LMS Student Portal</span>
                <h2 className="fy-serif-heading mb-3" style={{ color: '#ffffff', fontSize: '2rem' }}>Missed a Live Class? Your Learning Continues.</h2>
                <p className="fy-hero-para" style={{ color: '#C3DFC8' }}>
                  Work, family, or travel should never disrupt your momentum. Upon enrollment, you receive access to your personal <strong style={{ color: '#ffffff' }}>PranaFlow Student Dashboard</strong>.
                </p>

                <div className="row gy-2 mb-4" style={{ fontSize: '0.95rem', color: '#E8F5EB' }}>
                  <div className="col-sm-6"><div className="fy-check-bullet" style={{ color: '#E8F5EB' }}><i className="bi bi-check-circle-fill" style={{ color: '#A3D4B0' }}></i><span>Daily Live Lecture Links &amp; Schedule</span></div></div>
                  <div className="col-sm-6"><div className="fy-check-bullet" style={{ color: '#E8F5EB' }}><i className="bi bi-check-circle-fill" style={{ color: '#A3D4B0' }}></i><span>Same-Day Class Video Recordings</span></div></div>
                  <div className="col-sm-6"><div className="fy-check-bullet" style={{ color: '#E8F5EB' }}><i className="bi bi-check-circle-fill" style={{ color: '#A3D4B0' }}></i><span>Missed-Class Catch-Up Portal</span></div></div>
                  <div className="col-sm-6"><div className="fy-check-bullet" style={{ color: '#E8F5EB' }}><i className="bi bi-check-circle-fill" style={{ color: '#A3D4B0' }}></i><span>Revision Access During Course</span></div></div>
                  <div className="col-sm-6"><div className="fy-check-bullet" style={{ color: '#E8F5EB' }}><i className="bi bi-check-circle-fill" style={{ color: '#A3D4B0' }}></i><span>Downloadable Story &amp; Game Manuals</span></div></div>
                  <div className="col-sm-6"><div className="fy-check-bullet" style={{ color: '#E8F5EB' }}><i className="bi bi-check-circle-fill" style={{ color: '#A3D4B0' }}></i><span>Lesson Plan &amp; Assessment Submission</span></div></div>
                </div>

                <div style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 'var(--fy-radius-md)', padding: '20px 24px' }}>
                  <h4 style={{ fontSize: '1.05rem', fontWeight: 800, color: '#fbbf24', margin: '0 0 6px' }}>
                    Learn Live. Catch Up When Needed. Master Every Concept.
                  </h4>
                  <p style={{ margin: 0, fontSize: '0.9rem', color: '#C3DFC8', lineHeight: 1.6 }}>
                    Every live lecture is recorded and uploaded to your Student Dashboard on the same day so you can stay fully aligned with your batch.
                  </p>
                </div>
              </div>

              <div className="col-lg-5">
                <div className="fy-dashboard-card">
                  <h3 style={{ fontSize: '1.2rem', fontWeight: 800, color: 'var(--fy-sage-primary)', marginBottom: '16px' }}>Your Student Dashboard Includes:</h3>
                  <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px', display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '0.9rem', color: 'var(--fy-text-main)' }}>
                    <li className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>95-Hour Kids Yoga Course Access</span></li>
                    <li className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Daily Live Class Schedule &amp; Links</span></li>
                    <li className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>12 Structured Course Modules</span></li>
                    <li className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Uploaded Lecture Recordings</span></li>
                    <li className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Yoga Story &amp; Game Handouts</span></li>
                    <li className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Lesson Plan Assignment Submission</span></li>
                  </ul>
                  <button type="button" className="fy-btn-primary w-100" onClick={() => scrollTo('enrollment')}>
                    Get Access Upon Enrollment
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 9. MYTH-BUSTING */}
        <section className="fy-section">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <h2 className="fy-serif-heading">Kids Yoga Myth-Busting</h2>
              <div className="fy-title-underline"></div>
              <p className="fy-hero-para mt-3" style={{ maxWidth: '600px', margin: '12px auto 0' }}>
                Replacing common misconceptions about children's yoga with practical teaching clarity.
              </p>
            </div>

            <div className="row gy-3">
              {myths.map((item, idx) => (
                <div key={idx} className="col-md-6 col-lg-4">
                  <div className="fy-myth-card">
                    <span className="fy-myth-badge">MYTH vs FACT</span>
                    <h4 className="fy-myth-q">{item.m}</h4>
                    <p className="fy-myth-a"><strong>Fact:</strong> {item.f}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 10. WHY THIS COURSE IS DIFFERENT */}
        <section className="fy-section fy-section-alt">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <h2 className="fy-serif-heading">Why Can't I Just Find Kids Yoga Activities Online?</h2>
              <div className="fy-title-underline"></div>
            </div>

            <div className="fy-diff-grid">
              {diffPoints.map((card, idx) => (
                <div key={idx} className="fy-diff-card">
                  <h4 className="fy-diff-title">{card.title}</h4>
                  <p className="fy-diff-desc">{card.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 11. 95-HOUR ROADMAP */}
        <section className="fy-section">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <h2 className="fy-serif-heading">Your 95-Hour Kids Yoga Learning Journey</h2>
              <div className="fy-title-underline"></div>
            </div>

            <div className="fy-roadmap-grid">
              <div className="fy-roadmap-card month1">
                <div>
                  <span className="fy-roadmap-badge month1">Phase 1</span>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--fy-text-main)', margin: '0 0 10px' }}>Foundations &amp; Child Anatomy</h3>
                  <ul className="fy-week-list" style={{ marginBottom: '14px' }}>
                    <li>Module 01 — Introduction to Kids Yoga</li>
                    <li>Module 02 — Child Development Stages</li>
                    <li>Module 03 — Animal Asanas &amp; Safe Poses</li>
                    <li>Module 04 — Creative Storytelling Themes</li>
                  </ul>
                </div>
                <div style={{ background: 'var(--fy-sage-light)', border: '1px solid var(--fy-sage-border)', padding: '10px', borderRadius: 'var(--fy-radius-sm)', fontSize: '0.82rem', color: 'var(--fy-sage-dark)', fontWeight: 600 }}>
                  Goal: Master child growth anatomy &amp; storytelling.
                </div>
              </div>

              <div className="fy-roadmap-card month2">
                <div>
                  <span className="fy-roadmap-badge month2">Phase 2</span>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--fy-text-main)', margin: '0 0 10px' }}>Games, Mindfulness &amp; Management</h3>
                  <ul className="fy-week-list" style={{ marginBottom: '14px' }}>
                    <li>Module 05 — Yoga Games &amp; Group Activities</li>
                    <li>Module 06 — Balloon &amp; Bee Breath Practices</li>
                    <li>Module 07 — Mindfulness &amp; Quiet Savasana</li>
                    <li>Module 08 — Emotional Balance &amp; Yogic Values</li>
                  </ul>
                </div>
                <div style={{ background: 'var(--fy-blush-light)', border: '1px solid var(--fy-blush-border)', padding: '10px', borderRadius: 'var(--fy-radius-sm)', fontSize: '0.82rem', color: 'var(--fy-blush-dark)', fontWeight: 600 }}>
                  Goal: Build games, breathwork &amp; classroom control.
                </div>
              </div>

              <div className="fy-roadmap-card month3">
                <div>
                  <span className="fy-roadmap-badge month3">Phase 3</span>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--fy-text-main)', margin: '0 0 10px' }}>Lesson Plans, Practicum &amp; Certification</h3>
                  <ul className="fy-week-list" style={{ marginBottom: '14px' }}>
                    <li>Module 09 — Classroom Management &amp; Boundaries</li>
                    <li>Module 10 — Kids Yoga Lesson Planning</li>
                    <li>Module 11 — Teaching Practicum &amp; Live Practice</li>
                    <li>Module 12 — Final Assessment &amp; Certification</li>
                  </ul>
                </div>
                <div style={{ background: '#DCF5E6', border: '1px solid #A7D3B6', padding: '10px', borderRadius: 'var(--fy-radius-sm)', fontSize: '0.82rem', color: '#1A5C33', fontWeight: 600 }}>
                  Goal: Deliver live practicum &amp; earn 95-hour cert.
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 12. ENROLLMENT & SEAT RESERVATION */}
        <section id="pricing-form" className="fy-section fy-section-alt">
          <div className="fy-container text-center">
            <div id="enrollment" className="py-2">
              <div className="fy-section-title-wrap text-center mb-3">
                <h2 className="fy-serif-heading" style={{ fontSize: '2rem' }}>
                  Course Enrollment &amp; Seat Reservation
                </h2>
                <div className="fy-title-underline"></div>
              </div>

              <p style={{ fontSize: '1.02rem', color: 'var(--fy-text-muted)', maxWidth: '880px', margin: '0 auto 36px', lineHeight: 1.75 }}>
                Our 95-Hour Kids Yoga Teacher Training empowers you to teach engaging, age-appropriate yoga for children. Due to our commitment to personal attention and high-quality instruction, cohort sizes are strictly limited.
              </p>

              <div className="fy-enrollment-cta-group">
                <button
                  type="button"
                  className="fy-btn-primary py-3 px-4"
                  style={{ borderRadius: '30px', fontWeight: 700, fontSize: '0.95rem', whiteSpace: 'nowrap' }}
                  onClick={() => setIsEnrollModalOpen(true)}
                >
                  Enroll Now &amp; Reserve Your Seat <i className="bi bi-arrow-right-circle-fill ms-1"></i>
                </button>

                <button
                  type="button"
                  className="fy-btn-secondary py-3 px-4"
                  style={{ borderRadius: '30px', fontWeight: 700, fontSize: '0.95rem', whiteSpace: 'nowrap' }}
                  onClick={() => openModal(courseTitle, dbCourse)}
                >
                  Enquire Now <i className="bi bi-chat-dots ms-1"></i>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* 13. WHAT YOU RECEIVE */}
        <section className="fy-section">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <span className="fy-eyebrow">EVERYTHING INCLUDED</span>
              <h2 className="fy-serif-heading">What You Receive</h2>
              <div className="fy-title-underline"></div>
              <p className="fy-hero-para mt-3" style={{ maxWidth: '650px', margin: '12px auto 0' }}>
                Everything included in the 95-Hour Kids Yoga Teacher Training from the moment you enroll.
              </p>
            </div>

            <div className="fy-receive-grid">
              {highlights.map((item, idx) => (
                <div key={idx} className="fy-receive-card">
                  <div className="fy-receive-icon">
                    <i className="bi bi-check-lg"></i>
                  </div>
                  <span className="fy-receive-text">{item}</span>
                </div>
              ))}
            </div>

            <div className="fy-receive-assurance">
              <i className="bi bi-shield-check"></i>
              <span>All study manuals, live class links, game guides, and dashboard access are unlocked upon registration.</span>
            </div>
          </div>
        </section>

        {/* 14. CERTIFICATE SECTION */}
        <section className="fy-section fy-section-alt">
          <div className="fy-container">
            <div className="row align-items-center gy-4">
              <div className="col-lg-7">
                <span className="fy-eyebrow">ACADEMY CERTIFICATION</span>
                <h2 className="fy-serif-heading mb-3">95-Hour Kids Yoga TTC — Genuine &amp; Verified Certification</h2>
                <p className="fy-hero-para mb-3">
                  Your <strong>95-Hour Kids Yoga Teacher Training Certificate</strong> is issued through the applicable <strong>Yoga Alliance Registered Children’s Yoga School (RCYS) partner</strong> associated with your training batch.
                </p>
                <p style={{ color: 'var(--fy-text-main)', fontSize: '0.95rem', lineHeight: '1.65', marginBottom: '1rem' }}>
                  Before partnering with any school, PranaFlow Academy verifies its <strong>Yoga Alliance RCYS registration status</strong> and reviews its experience, teaching standards, faculty, and specialization in children’s yoga education.
                </p>
                <p style={{ color: 'var(--fy-text-main)', fontSize: '0.95rem', lineHeight: '1.65', marginBottom: '1rem' }}>
                  The applicable RCYS partner connected with your batch will be shared during your classes and will also be visible in your student dashboard.
                </p>
                <p style={{ color: 'var(--fy-text-main)', fontSize: '0.95rem', lineHeight: '1.65', marginBottom: '1rem' }}>
                  After successfully completing the required training hours, attendance, assignments, practicum, teaching requirements, and final assessment, you will receive a <strong>genuine certificate issued through the applicable verified Yoga Alliance Registered Children’s Yoga School partner</strong>.
                </p>
                <p style={{ color: 'var(--fy-text-main)', fontSize: '0.95rem', lineHeight: '1.65', marginBottom: '1.5rem' }}>
                  Completion of the 95-hour training may fulfil the children’s yoga training component applicable to the Yoga Alliance RCYT pathway, subject to the student meeting Yoga Alliance’s additional individual eligibility and registration requirements.
                </p>

                <div className="fy-trust-badges mt-2 mb-2" style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', background: 'var(--fy-cream-card)', padding: '12px 20px', borderRadius: '30px', border: '1px solid var(--fy-sage-border)', fontSize: '0.88rem', fontWeight: '600', color: 'var(--fy-sage-dark)' }}>
                  <i className="bi bi-patch-check-fill text-success" style={{ fontSize: '1.1rem' }}></i>
                  <span>Yoga Alliance RCYS Partner · Verified School · 95-Hour Specialized Training · Genuine Certification</span>
                </div>
              </div>

              <div className="col-lg-5 text-center">
                <div style={{ background: 'var(--fy-cream-card)', border: '2px solid var(--fy-sage-border)', borderRadius: 'var(--fy-radius-lg)', padding: '36px 28px', boxShadow: 'var(--fy-shadow-card)' }}>
                  <i className="bi bi-award-fill" style={{ fontSize: '3.8rem', color: 'var(--fy-sage-primary)' }}></i>
                  <h3 style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--fy-sage-dark)', marginTop: '16px', marginBottom: '8px' }}>
                    95-Hour Kids Yoga Teacher Certificate
                  </h3>
                  <div style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--fy-sage-primary)', marginBottom: '16px' }}>Issued via Verified Yoga Alliance RCYS Partner School</div>
                  <p style={{ fontSize: '0.88rem', color: 'var(--fy-text-main)', lineHeight: 1.6, margin: 0 }}>
                    Awarded upon completing 95 hours of specialized kids yoga training, live practicum, assignments, and final evaluation through our verified RCYS partner school.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 15. FAQ */}
        <section id="faq" className="fy-section">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <h2 className="fy-serif-heading">Frequently Asked Questions</h2>
              <div className="fy-title-underline"></div>
            </div>

            <div className="fy-faq-grid">
              {faqs.map((faq, idx) => (
                <div key={idx} className="fy-faq-item">
                  <button className="fy-faq-btn" aria-expanded={activeFaq === idx}
                    type="button"
                    onClick={() => setActiveFaq(activeFaq === idx ? null : idx)}>
                    <span>{faq.q}</span>
                    <div className="fy-faq-icon"><i className="bi bi-plus"></i></div>
                  </button>
                  {activeFaq === idx && (
                    <div className="fy-faq-ans" style={{ display: 'block' }}>
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 16. FINAL CTA BANNER */}
        <section className="fy-section fy-section-alt">
          <div className="fy-container">
            <div className="fy-cta-banner">
              <div className="row gy-4 align-items-center">
                <div className="col-lg-8">
                  <h2 className="fy-cta-heading">Don't Just Learn Children's Yoga Poses. Learn How to Teach Children Yoga.</h2>
                  <p className="fy-cta-sub">Understand the Child. Make Yoga Creative. Build Better Classes. Develop Teaching Confidence.</p>
                  <p style={{ fontSize: '0.95rem', color: '#C3DFC8', lineHeight: 1.7, marginBottom: '20px' }}>
                    Master child anatomy, age-based storytelling, yoga games, balloon breathing, classroom management, and teaching practicum with expert guidance managed in your PranaFlow Student Dashboard.
                  </p>
                  <div className="fy-btn-group">
                    <button type="button" className="fy-btn-primary fy-cta-btn-white" onClick={() => setIsEnrollModalOpen(true)}>
                      Enroll Now <i className="bi bi-arrow-right-circle-fill"></i>
                    </button>
                    <button type="button" className="fy-btn-secondary fy-cta-btn-outline" onClick={() => openModal(courseTitle, dbCourse)}>
                      Enquire Now
                    </button>
                  </div>
                </div>
                <div className="col-lg-4 text-center">
                  <div style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 'var(--fy-radius-lg)', padding: '24px 20px' }}>
                    <div className="fy-cta-logo-wrap">
                      <img 
                        src={getLogoUrl()} 
                        alt="PranaFlow Academy" 
                        className="fy-cta-card-logo"
                        onError={(e) => { e.target.onerror = null; e.target.src = '/apple-touch-icon.png'; }}
                      />
                    </div>
                    <div style={{ fontWeight: 800, fontSize: '1.1rem', color: '#ffffff', marginBottom: '4px' }}>95 Hours</div>
                    <div style={{ fontSize: '0.85rem', color: '#C3DFC8' }}>Comprehensive Training</div>
                    <div style={{ fontSize: '0.85rem', color: '#C3DFC8' }}>12 Detailed Modules</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 17. CHILD SAFETY NOTICE */}
        <div className="fy-container mb-5">
          <div className="fy-page-disclaimer">
            <strong>Important Child Safety &amp; Scope Notice:</strong> This course provides specialized educational teacher training in children's yoga, movement, and mindfulness. It does not qualify a graduate to diagnose or treat childhood psychological, neurological, or developmental disorders, or replace licensed child therapy or school safeguarding credentials. All kids yoga instruction must comply with applicable institutional policies, child protection laws, and local regulations.
          </div>
        </div>

      </main>

      <CourseEnrollModal
        isOpen={isEnrollModalOpen}
        onClose={() => setIsEnrollModalOpen(false)}
        courseTitle={courseTitle}
        formData={formData}
        setFormData={setFormData}
        upcomingBatches={upcomingBatches}
        isSubmitting={isSubmitting}
        isSuccess={isSuccess}
        handleEnrollSubmit={handleEnrollSubmit}
      />

      <PublicFooter />
    </div>
  );
};

export default KidsYoga;
